/**
 * InstallPrompt.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * PWA INSTALL BANNER — Banner fijo en la parte inferior para instalar StarTip.
 *
 * CAMBIO CLAVE: Ya no registra su propio 'beforeinstallprompt' listener.
 * Usa el hook usePwaInstallPrompt (fuente de verdad única) para obtener:
 *   - canInstallPwa: true cuando Chrome/Edge tiene el prompt disponible
 *   - showInstallPrompt(): llamar SOLO desde un onClick de usuario
 *
 * Aparece cuando:
 *   a) Chrome/Edge/Android detecta que la PWA es instalable (beforeinstallprompt)
 *   b) iOS Safari: muestra instrucciones del share-sheet
 *   c) Evento 'startip:payment-complete': mayor engagement → siempre muestra
 *
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { useState, useEffect } from "react";
import { X, Download, Share } from "lucide-react";
import { usePwaInstallPrompt } from "@/hooks/usePwaInstallPrompt";
import { useTranslation } from "react-i18next";

const DISMISSED_KEY = "startip_install_dismissed";

export function InstallPrompt() {
  // ── PWA install state desde el hook centralizado ──────────────────────────
  const { canInstallPwa, showInstallPrompt, isInstalled } = usePwaInstallPrompt();
  const { t } = useTranslation();

  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [fromPayment, setFromPayment] = useState(false);

  useEffect(() => {
    // No hacer nada si ya está instalado
    if (isInstalled) return;

    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // ── Auto-mostrar en iOS (instrucciones share-sheet) ────────────────────
    let iosTimer;
    if (ios && !sessionStorage.getItem(DISMISSED_KEY)) {
      iosTimer = setTimeout(() => setShow(true), 3000);
    }

    // ── Mostrar cuando el prompt esté disponible en Chrome/Edge ───────────
    // El banner aparece 1.5 s después de que beforeinstallprompt se recibe.
    // El hook ya captura ese evento — aquí solo reaccionamos a canInstallPwa.
    if (canInstallPwa && !sessionStorage.getItem(DISMISSED_KEY)) {
      const t = setTimeout(() => setShow(true), 1500);
      return () => {
        clearTimeout(t);
        clearTimeout(iosTimer);
      };
    }

    return () => clearTimeout(iosTimer);
  }, [canInstallPwa, isInstalled]);

  useEffect(() => {
    if (isInstalled) return;

    // ── Payment completion: highest-engagement trigger ─────────────────────
    // SuccessPage y V2TipSuccess disparan este evento después de un pago.
    // NO llaman a .prompt() directamente: solo lanzan el evento para que
    // este banner aparezca y el usuario pulse "Instalar" con su propio gesto.
    const handlePayment = () => {
      if (isInstalled) return;
      sessionStorage.removeItem(DISMISSED_KEY);
      setFromPayment(true);
      setTimeout(() => setShow(true), 2500);
    };
    window.addEventListener("startip:payment-complete", handlePayment);

    return () => window.removeEventListener("startip:payment-complete", handlePayment);
  }, [isInstalled]);

  const dismiss = () => {
    setShow(false);
    setFromPayment(false);
    sessionStorage.setItem(DISMISSED_KEY, "1");
  };

  /**
   * install()
   * ─────────────────────────────────────────────────────────────────────────
   * DEBE llamarse ÚNICAMENTE desde el onClick de un botón (gesto de usuario).
   * showInstallPrompt() del hook garantiza que .prompt() se llame correctamente.
   */
  const install = async () => {
    const { outcome } = await showInstallPrompt();
    if (outcome === "accepted") {
      setShow(false);
    }
    setShow(false);
  };

  if (isInstalled || !show) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 px-4 pb-4 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="install-prompt-banner"
    >
      <div className="max-w-lg mx-auto bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Accent bar — gold after payment, pink otherwise */}
        <div className={`h-0.5 w-full bg-gradient-to-r ${
          fromPayment
            ? "from-yellow-400 via-yellow-300 to-transparent"
            : "from-primary via-neon-pink to-transparent"
        }`} />

        <div className="p-4 flex items-start gap-3">
          {/* Logo */}
          <img
            src="/icons/icon-192.png"
            alt="StarTip"
            className="w-11 h-11 rounded-xl shrink-0"
          />

          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-white text-sm">
              {fromPayment ? t("install.title") : t("install.title")}
            </p>

            {isIOS ? (
              <p className="text-xs text-zinc-400 font-body mt-1 leading-relaxed">
                {fromPayment && (
                  <span className="block text-yellow-400 font-semibold mb-1">
                    ¡Gracias por tu propina!
                  </span>
                )}
                Toca{" "}
                <span className="inline-flex items-center gap-0.5 text-primary font-bold">
                  <Share className="w-3 h-3 inline" /> Compartir
                </span>{" "}
                en Safari y luego{" "}
                <span className="text-white font-semibold">
                  "Añadir a pantalla de inicio"
                </span>.
              </p>
            ) : (
              <p className="text-xs text-zinc-400 font-body mt-0.5">
                {t("install.subtitle")}
              </p>
            )}

            {!isIOS && canInstallPwa && (
              <button
                onClick={install}
                className={`mt-2.5 flex items-center gap-1.5 px-4 py-2 text-xs font-heading font-bold uppercase rounded-lg active:scale-95 transition-all ${
                  fromPayment
                    ? "bg-yellow-400 text-black hover:bg-yellow-300"
                    : "bg-primary text-white hover:bg-primary/90"
                }`}
                data-testid="install-app-btn"
              >
                <Download className="w-3.5 h-3.5" />
                {t("install.install")}
              </button>
            )}
          </div>

          <button
            onClick={dismiss}
            className="p-1.5 text-zinc-600 hover:text-zinc-300 transition-colors shrink-0"
            aria-label={t("install.close")}
            data-testid="install-prompt-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
